"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import AceEditor from "react-ace";
import { twMerge } from "tailwind-merge";

const LANGUAGE_MODULES: Record<CodeLanguage, () => Promise<unknown>> = {
  json: () => import("ace-builds/src-noconflict/mode-json"),
  javascript: () => import("ace-builds/src-noconflict/mode-javascript"),
  typescript: () => import("ace-builds/src-noconflict/mode-typescript"),
  html: () => import("ace-builds/src-noconflict/mode-html"),
  css: () => import("ace-builds/src-noconflict/mode-css"),
  markdown: () => import("ace-builds/src-noconflict/mode-markdown"),
  yaml: () => import("ace-builds/src-noconflict/mode-yaml"),
  xml: () => import("ace-builds/src-noconflict/mode-xml"),
  python: () => import("ace-builds/src-noconflict/mode-python"),
  sql: () => import("ace-builds/src-noconflict/mode-sql"),
};

const THEME_MODULES: Record<CodeTheme, () => Promise<unknown>> = {
  github: () => import("ace-builds/src-noconflict/theme-github"),
  github_dark: () => import("ace-builds/src-noconflict/theme-github_dark"),
};

const EXTENSION_MODULES: Record<string, () => Promise<unknown>> = {
  "ext-language-tools": () => import("ace-builds/src-noconflict/ext-language_tools"),
};

const loadedModules = new Set<string>();

async function ensureModuleLoaded(key: string, loader: () => Promise<unknown>) {
  if (loadedModules.has(key)) return;
  await loader();
  loadedModules.add(key);
}

async function prepareAceResources(language: CodeLanguage, theme: CodeTheme, enableLanguageTools: boolean) {
  await Promise.all([
    ensureModuleLoaded(`language:${language}`, LANGUAGE_MODULES[language]),
    ensureModuleLoaded(`theme:${theme}`, THEME_MODULES[theme]),
    enableLanguageTools
      ? ensureModuleLoaded("extension:language-tools", EXTENSION_MODULES["ext-language-tools"])
      : undefined,
  ]);
}

function areResourcesLoaded(language: CodeLanguage, theme: CodeTheme, enableLanguageTools: boolean) {
  const baseReady = loadedModules.has(`language:${language}`) && loadedModules.has(`theme:${theme}`);

  if (!enableLanguageTools) {
    return baseReady;
  }

  return baseReady && loadedModules.has("extension:language-tools");
}

export type CodeLanguage =
  | "json"
  | "javascript"
  | "typescript"
  | "html"
  | "css"
  | "markdown"
  | "yaml"
  | "xml"
  | "python"
  | "sql";

export type CodeTheme = "github" | "github_dark";

export interface CodeEditorProps {
  /**
   * The code content to display
   */
  value: string;
  /**
   * Whether the editor is read-only
   */
  readOnly?: boolean;
  /**
   * Callback when the content changes
   */
  onChange?: (value: string) => void;
  /**
   * The programming language mode
   * @default "json"
   */
  language?: CodeLanguage;
  /**
   * The editor theme
   * @default "github_dark"
   */
  theme?: CodeTheme;
  /**
   * The height of the editor. If not provided, height will be calculated based on content.
   */
  height?: string;
  /**
   * The width of the editor
   * @default "100%"
   */
  width?: string;
  /**
   * Additional className to apply
   */
  className?: string;
  /**
   * Line height in pixels for auto-height calculation
   * @default 19
   */
  lineHeight?: number;
  /**
   * Minimum number of lines to show when auto-sizing
   * @default 3
   */
  minLines?: number;
  /**
   * Maximum number of lines to show when auto-sizing
   * @default 50
   */
  maxLines?: number;
  /**
   * Whether to show line numbers
   * @default true
   */
  showLineNumbers?: boolean;
  /**
   * Whether to show the gutter
   * @default true
   */
  showGutter?: boolean;
  /**
   * Font size for the editor
   * @default 14
   */
  fontSize?: number;
  /**
   * Whether to wrap long lines
   * @default false
   */
  wrapEnabled?: boolean;
}

/**
 * CodeEditor
 *
 * A code editor component with syntax highlighting and various language support.
 * Built on top of Ace Editor.
 */
export function CodeEditor({
  value,
  readOnly = false,
  onChange,
  language = "json",
  theme = "github_dark",
  height,
  width = "100%",
  className,
  lineHeight = 19,
  minLines = 3,
  maxLines = 50,
  showLineNumbers = true,
  showGutter = true,
  fontSize = 14,
  wrapEnabled = false,
}: CodeEditorProps) {
  const editorRef = useRef<AceEditor>(null);
  const [isReady, setIsReady] = useState(() => areResourcesLoaded(language, theme, !readOnly));

  // Calculate height based on content if no height provided
  const calculatedHeight = useMemo(() => {
    if (height) return height;

    const lineCount = (value.match(/\n/g) || []).length + 1;
    const lines = Math.max(minLines, Math.min(lineCount, maxLines));
    // Add some padding for the scrollbar
    return `${lines * lineHeight + 10}px`;
  }, [value, height, lineHeight, minLines, maxLines]);

  useEffect(() => {
    let cancelled = false;
    const enableLanguageTools = !readOnly;

    if (areResourcesLoaded(language, theme, enableLanguageTools)) {
      setIsReady(true);
      return () => {
        cancelled = true;
      };
    }

    setIsReady(false);

    void prepareAceResources(language, theme, enableLanguageTools)
      .then(() => {
        if (!cancelled) {
          setIsReady(true);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setIsReady(true);
        }

        console.error("Failed to load Ace editor resources", error);
      });

    return () => {
      cancelled = true;
    };
  }, [language, readOnly, theme]);

  useEffect(() => {
    if (!isReady) return;

    if (editorRef.current?.editor) {
      editorRef.current.editor.resize();
    }
  }, [calculatedHeight, isReady]);

  return (
    <div
      className={twMerge("overflow-hidden rounded-md border border-border-input", className)}
      style={{ width, height: calculatedHeight }}
    >
      {isReady ? (
        <AceEditor
          ref={editorRef}
          mode={language}
          theme={theme}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          width="100%"
          height="100%"
          fontSize={fontSize}
          showPrintMargin={false}
          showGutter={showGutter}
          highlightActiveLine={!readOnly}
          setOptions={{
            useWorker: true,
            showLineNumbers,
            tabSize: 2,
            enableBasicAutocompletion: !readOnly,
            enableLiveAutocompletion: !readOnly,
            enableSnippets: !readOnly,
            showFoldWidgets: true,
            wrap: wrapEnabled,
          }}
          editorProps={{
            $blockScrolling: true,
          }}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-surface-muted text-foreground-muted">
          Loading editor…
        </div>
      )}
    </div>
  );
}
